import React, { useEffect, useMemo, useState } from "react";
import ShortcutDialogBody from "./shortcutDialogBody";
import { ShortcutUpdateModelv1, defaultShortcut } from "modules/shortcuts/models";
import { Audience } from "modules/audiences";
import DialogSkeleton from "./dialogSkeleton";
import useIsMounted from "modules/common/hooks/useIsMounted";
import { isUrlValid } from "utils/isUrlValid";
import ShallowEquals from "utils/shallowEquals";

interface IShortcutDialogSkeletonProps {
    show: boolean;
    onClose: () => void;
    onDialogActionButtonClick: (input: ShortcutUpdateModelv1) => void;
    audiences: Audience[];
    input?: ShortcutUpdateModelv1;
    dialogTitle: string;
    dialogActionButtonLabel: string;
    disableActionButtonIfInputNotChanged?: boolean;
}

const ShortcutDialogSkeleton: React.FC<IShortcutDialogSkeletonProps> = ({
    show,
    onClose,
    onDialogActionButtonClick,
    audiences,
    input = defaultShortcut,
    dialogTitle,
    dialogActionButtonLabel,
    disableActionButtonIfInputNotChanged
}) => {
    const [previousShortcutInput, setPreviousShortcutInput] = useState<ShortcutUpdateModelv1>(input);
    const [shortcutInput, setShortcutInput] = useState<ShortcutUpdateModelv1>(input);
    const isMounted = useIsMounted();

    const hasShortcutInputChanged = useMemo(
        () => {
            if (disableActionButtonIfInputNotChanged === undefined) 
                return true;

            const audienceIsEqual = (newAudiences: string[]): boolean => {       
                if (newAudiences.length !== shortcutInput.audiences.length)
                    return false;
        
                const originalAudIdSet = new Set(shortcutInput.audiences);
        
                return newAudiences.every(id => originalAudIdSet.has(id));
            }
            
            return !ShallowEquals(previousShortcutInput.groupId, shortcutInput.groupId) ||
                !ShallowEquals(previousShortcutInput.name, shortcutInput.name) ||
                !ShallowEquals(previousShortcutInput.url, shortcutInput.url) ||
                !ShallowEquals(previousShortcutInput.openInNewWindow, shortcutInput.openInNewWindow) ||
                !audienceIsEqual(previousShortcutInput.audiences);
        }, 
        [disableActionButtonIfInputNotChanged, previousShortcutInput, shortcutInput]
    );

    useEffect(() => {
        setShortcutInput(input);
    }, [isMounted, input]);

    const isNotFilled = !shortcutInput.groupId?.trim() ||
        !shortcutInput.name?.trim() ||
        !shortcutInput.url?.trim() ||
        !isUrlValid(shortcutInput.url);

    const onDialogClose = () => {
        setShortcutInput(input);
        onClose();
    }

    const onDialogAction = () => {
        onDialogActionButtonClick(shortcutInput);
        setShortcutInput(input);
    }

    return <DialogSkeleton
        show={show}
        onClose={onDialogClose}
        onDialogActionButtonClick={onDialogAction}
        dialogTitle={dialogTitle}
        dialogActionButtonLabel={dialogActionButtonLabel}
        disableActionButton={!hasShortcutInputChanged || isNotFilled}
    >
        <ShortcutDialogBody
            input={shortcutInput}
            setInput={setShortcutInput}
            availableAudiences={audiences}
        />
    </DialogSkeleton>;
};

export default ShortcutDialogSkeleton;